import {useCallback, useMemo, useRef, useState} from 'react';
import type {
  InputChangeActionMeta,
  RegisteredFieldHandlers,
  UseVirtualKeyboardSearchSelectsOptions,
} from './types';

export function useVirtualKeyboardSearchSelects({
  fieldIds,
  onBlur,
}: UseVirtualKeyboardSearchSelectsOptions) {
  const [values, setValues] = useState<Record<string, string>>({});
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const fields = useMemo(
    () =>
      Object.fromEntries(
        fieldIds.map(id => [
          id,
          {
            getValue: () => valuesRef.current[id] ?? '',
            setValue: (v: string) => setValues(prev => ({...prev, [id]: v})),
            onBlur: () => onBlur?.(),
          },
        ]),
      ),
    [fieldIds, onBlur],
  );

  const setValue = useCallback((fieldId: string, value: string) => {
    setValues(prev => ({...prev, [fieldId]: value}));
  }, []);

  const register = useCallback(
    (fieldId: string): RegisteredFieldHandlers => {
      if (!fieldIds.includes(fieldId)) {
        console.warn(
          `[useVirtualKeyboardSearchSelects] fieldId "${fieldId}" is not in fieldIds: [${fieldIds.join(
            ', ',
          )}]`,
        );
      }
      return {
        value: values[fieldId] ?? '',
        setValue: (v: string) => setValue(fieldId, v),
        onInputChange: (newValue: string, actionMeta: InputChangeActionMeta) => {
          if (actionMeta.action !== 'input-blur' && actionMeta.action !== 'set-value') {
            setValue(fieldId, newValue);
          }
        },
        onChange:
          <T>(userOnChange?: (option: T | null) => void) =>
          (option: T | null) => {
            setValue(fieldId, '');
            userOnChange?.(option);
          },
      };
    },
    [fieldIds, values, setValue],
  );

  return {
    register,
    getField: register,
    fields,
  };
}
